import React, { useState, useEffect } from 'react';
import Header from '../components/header';
import { 
  Button,
  Dialog,
  MenuItem,
  Paper,
  Select,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  TextField} from '@mui/material';
import store, { useAppDispatch, useAppSelector } from '../store';
import { setActiveCabinet, setIsSearching } from '../store/reducers/cabinetSlice';
import { setActiveOrganization } from '../store/reducers/organizationSlice';
import { setStores } from '../store/reducers/storeSlice';
import { Cabinet } from '../models/cabinet';
import { IStore } from '../models/store';
import { getCabinets, getStores, getCabinetBySerial } from '../utilities/api-utils';
import CabinetDialog from '../components/dialogs/cabinet-dialog';
import NewCabinetDialog from '../components/dialogs/new-cabinet-dialog';

type SearchParams = {
  organizationId: string,
  storeId: string,
};

function CabinetsView() {
  const dispatch = useAppDispatch();

  const [allStores, setAllStores] = useState<IStore[]>([]);
  const [selectedStoreId, setSelectedStoreId] = useState<string>('');
  const [cabinets, setCabinets] = useState<Cabinet[]>([]);
  const [filteredCabinets, setFilteredCabinets] = useState<Cabinet[]>([]);
  const [dialog, setDialog] = useState<boolean>(false);
  const [newCabinetDialog, setNewCabinetDialog] = useState<boolean>(false);
  const [serialFilter, setSerialFilter] = useState<string>('');
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const { activeOrganization } = useAppSelector(store => store.organization);
  const { activeCabinet } = useAppSelector(store => store.cabinet);
  const { isSearching } = useAppSelector(store => store.cabinet);
  
  const getAllStores = async () => {
    const stores = await getStores((store.getState().authorization.cloudKey));
    const filteredStores = stores.data.filter(x => x.organization_id === activeOrganization);
    const sorted = filteredStores.sort((a, b) => {
      const textA = a.name.toUpperCase();
      const textB = b.name.toUpperCase();
      return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
    });
    const storeIds = stores.data.map(x => x.id);
    dispatch(setStores(storeIds));
    setAllStores(sorted);

    if (!searchParams?.storeId) {
      setSelectedStoreId(sorted[0].id);
    } else {
      setSelectedStoreId(searchParams?.storeId);
    }
  };

  const storeSelected = async (id: string) => {
    const store = allStores.find(x => x.id === id);
    setSelectedStoreId(store!.id);
  };

  const getAllCabinets = async () => {
    dispatch(setIsSearching(false));

    if (selectedStoreId) {
      const cabs = await getCabinets(store.getState().authorization.cloudKey, '', selectedStoreId);
      const tempCabs = [];
      for (const cabinet of cabs.data) {
        const concreteCabinet = new Cabinet(cabinet);
        tempCabs.push(concreteCabinet);
      }

      const sorted = tempCabs.sort((a, b) => {
        const textA = a.business_name?.toUpperCase();
        const textB = b.business_name?.toUpperCase();
        if (!textA || !textB) {
          return 1;
        }
        return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
      });

      setCabinets(sorted);
      setFilteredCabinets(sorted.filter((x) => x.store_id === selectedStoreId));
      if (cabs.data.length > 0) {
        dispatch(setActiveCabinet(sorted[0].id));
      }
    }
  };

  useEffect(() => {
    if (!searchParams?.storeId) {
      const asyncGetCabs = async() => {
        await getAllCabinets();
      };
      asyncGetCabs();
    } else {
      const params: SearchParams = {
        organizationId: '',
        storeId: ''
      };

      setSearchParams(params);
    }
  }, [selectedStoreId]);
  
  useEffect(() => {
    const asyncGetStores = async() => {
      await getAllStores();
    };
    asyncGetStores();
  }, [activeOrganization]);

  useEffect(() => {
    if (searchParams?.organizationId) {
      dispatch(setActiveOrganization(searchParams?.organizationId));
    }
  }, [searchParams]);

  const newCabinet = () => {
    dispatch(setActiveCabinet(''));
    setNewCabinetDialog(true);
  };

  const newCabinetCreated = (cabinetId: string) => {
    setNewCabinetDialog(false);
    dispatch(setActiveCabinet(cabinetId));
    refresh();
  };

  const cabinetSelected = (cabinet: Cabinet) => {
    dispatch(setActiveCabinet(cabinet.id));
    setDialog(true);
  };

  const refresh = async() => {
    await getAllStores();
    await getAllCabinets();
  };
  
  const updateFilter = (serial: string) => {
    setSerialFilter(serial);
  };

  const search = async () => {
    if (!serialFilter) {
      return;
    }

    dispatch(setIsSearching(true));

    const searchResult = await getCabinetBySerial(store.getState().authorization.cloudKey, serialFilter);
    const searchCabinet = [];
    for (const cabinet of searchResult.data) {
      const concreteCabinet = new Cabinet(cabinet);
      searchCabinet.push(concreteCabinet);
    }
    
    if (searchCabinet) {
      const params: SearchParams = {
        organizationId: searchCabinet[0].organization?.id ?? '',
        storeId: searchCabinet[0].store_id
      };

      setSearchParams(params);
      setFilteredCabinets(searchCabinet);
    } else {
      alert(`No cabinet found matching the serial ${serialFilter}`);
    }

    if (isSearching) {
      updateFilter('');
      dispatch(setIsSearching(false));
      refresh();
    }
  };
  
  return (
    <div >
      <Header title='Cabinets' />
      <Dialog
        sx={{
          marginLeft: '100px',
          width: '100%'
        }}
        fullWidth
        maxWidth='lg'
        open={dialog}
        onClose={() => setDialog(false)}>
        <CabinetDialog editingCabinet={activeCabinet} setDialog={setDialog} refresh={refresh}  selectedStoreId={selectedStoreId} />
      </Dialog>
      <Dialog
        sx={{
          marginLeft: '100px',
          width: '100%'
        }}
        fullWidth
        maxWidth='lg'
        open={newCabinetDialog}
        onClose={() => setNewCabinetDialog(false)}>
        <NewCabinetDialog selectedStoreId={selectedStoreId} setDialog={setNewCabinetDialog} finish={newCabinetCreated} />
      </Dialog>
      <Select
        disabled={isSearching}
        sx={{
          color: 'black',
          width: '200px',
          marginRight: '20px',
          height: '60px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'black !important',
          },
          '& .MuiSelect-iconOutlined': {
            color: 'black'
          }
        }}
        value={selectedStoreId}
        onChange={async(e) => {
          await storeSelected(e.target.value);
        }}>
        {allStores.map((selectStore, index) => (
          <MenuItem 
            key={index}
            value={selectStore.id}>
              {selectStore.name}
          </MenuItem>
        ))}
      </Select>
      <Button
        variant='contained'
        sx={{
          height: '60px',
          width: '175px',
          fontSize: '20px',
          marginTop: '10px',
          marginBottom: '15px'
        }}
        onClick={newCabinet}>
        New Cabinet
      </Button>
      <TextField
        sx={{
          width: '250px',
          marginTop: '15px',
          marginBottom: '25px',
          marginLeft: '20px',
        }}
        label='Serial Filter'
        value={serialFilter}
        onChange = {(e) => {
          updateFilter(e.target.value);
        }} />
      <Button
        variant='contained'
        sx={{
          height: '60px',
          width: '175px',
          fontSize: '20px',
          marginTop: '10px',
          marginLeft: '5px',
          marginBottom: '10px'
        }}
        onClick={search}>
        { isSearching ? 'Clear' : 'Search' }
      </Button>

      <TableContainer component={Paper} sx={{maxHeight: '600px'}}>
        <Table
          sx={{minWidth: '600px'}}>
          <TableHead>
            <TableRow>
              <TableCell align='left'>Device Serial</TableCell>
              <TableCell align='left'>Business</TableCell>
              <TableCell align='left'>Description</TableCell>
              <TableCell align='left'>Location</TableCell>
              <TableCell align='center'>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredCabinets.map((cabinet, index) => (
              <TableRow key={index}>
                <TableCell>
                  {cabinet.getTabletSerial()}
                </TableCell>
                <TableCell>
                  {cabinet.business_name}
                </TableCell>
                <TableCell>
                  {cabinet.description}
                </TableCell>
                <TableCell>
                  {cabinet.cabinet_location}
                </TableCell>
                <TableCell align='center' sx={{padding: '0'}}>
                  <Button
                    variant='contained'
                    onClick={() => cabinetSelected(cabinet)}>
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
}

export default CabinetsView;
